import React from 'react';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import ScheduledItem from './ScheduledItem';
import UserMessage from '../../common/UserMessage/UserMessage';
import LoadingComponent from '../../common/LoadingComponent/LoadingComponent';
import { get, has } from 'lodash';
import appLabels from '../../utils/appLabels';
import PaymentErrorMessageComponent from '../../common/PaymentErrorMessage/PaymentErrorMessage';

const titleStyle = css`
  color: #2a2928 !important;
  margin-top: 1.5rem;
  font-size: 1.25rem;
  line-height: 1.5rem;
  font-weight: 600 !important;
  margin-bottom: 0.5rem !important;

  @media (max-width: 641px) {
    font-size: 1rem;
  }
`;

const subtitleStyle = css`
  color: #2a2928 !important;
  font-size: 0.875rem;

  @media (max-width: 641px) {
    font-size: 0.75rem;
  }
`;

const itemsListStyle = css`
  display: flex;
  flex-direction: column;
  justify-items: center;
  width: 100%;
`;

const messageStyle = css`
  margin-top: 1rem;
`;

const otaItemsListStyle = css`
  & > div:last-child {
    border-bottom: none;
  }
`;

const paymentErrorMessagContainereMarginStyle = css`
  margin-top: 24px;
`;

const ScheduledItemsList = (props) => {
  const loading = get(props, 'loading', false);
  const items = get(props, 'items', []);
  const showRemoveItemModal = get(props, 'showRemoveItemModal', false);
  const refreshHandler = get(props, 'refreshHandler', () => {});
  const cancellationReasons = get(props, 'cancellationReasons', []);
  const showSkipOnceLink = get(props, 'showSkipOnceLink', false);
  const showSubstitutionButton = get(props, 'showSubstitutionButton', false);
  const showDeleteIcon = get(props, 'showDeleteIcon');
  const showHeaders = get(props, 'showHeaders', true);
  const apiError = get(props, 'apiError', false);
  const displayItemFrequency = get(props, 'displayItemFrequency', true);
  const editableQuantity = get(props, 'editableQuantity', true);
  const displaySubstitutionCheckbox = get(props, 'displaySubstitutionCheckbox', false);
  const displayCancelButton = get(props, 'displayCancelButton', true);
  const showEditSubstitution = get(props, 'showEditSubstitution', 'NONE');
  const substitutionValue = get(props, 'substitutionValue', 'NONE');
  const quantityResetOnError = get(props, 'quantityResetOnError', false);
  const isOneTimeAddList = get(props, 'isOneTimeAddList', false);
  const upcomingOrders = get(props,'upcomingOrders', null);
  const paymentFailure = get(upcomingOrders, 'preCheckFailReason') === 'PAYMENT_FAILURE';
  const orderType = get(props, 'orderType', 'upcoming'); // 'upcoming', 'future', or 'all'


  if (apiError)
    return (
      <UserMessage
        className={messageStyle}
        messageText={appLabels.ApiErrorMessages.scheduleItemList}
        messageType={'warning'}
      />
    );

  return (
    <div>
    {paymentFailure && <PaymentErrorMessageComponent className={paymentErrorMessagContainereMarginStyle}/>}
     {showHeaders ? (
        <div>
          <p className={titleStyle}>Manage items for all Schedule & Save orders.</p>
          <p className={subtitleStyle}>
            All prices are estimated. Final pricing determined one day before scheduled order.
          </p>
        </div>
      ) : null}

      <div className={`${itemsListStyle} ${isOneTimeAddList && otaItemsListStyle}`}>
        {loading ? (
          <LoadingComponent />
        ) : (
          items.map((item) => {
            const shouldDisplayFrequency = displayItemFrequency ? has(item, 'frequency') : false;

            return (
              <ScheduledItem
                refreshHandler={refreshHandler}
                showRemoveItemModal={showRemoveItemModal}
                item={item}
                key={item.subscriptionId}
                showDeleteIcon={showDeleteIcon}
                showSkipOnceLink={showSkipOnceLink}
                showSubstitutionButton={showSubstitutionButton}
                cancellationReasons={cancellationReasons}
                displayItemDate={has(item, 'nextFulfillmentDate')}
                displayItemFrequency={shouldDisplayFrequency}
                editableQuantity={editableQuantity}
                displaySubstitutionCheckbox={displaySubstitutionCheckbox}
                displayCancelButton={displayCancelButton}
                showEditSubstitution={showEditSubstitution}
                substitutionValue={substitutionValue}
                quantityResetOnError={quantityResetOnError}
                isOneTimeAddList={isOneTimeAddList}
                otaToSnsFlag={get(item, 'otaToSnsFlag', false)}
                orderType={orderType}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

ScheduledItemsList.propTypes = {
  children: PropTypes.any,
  items: PropTypes.array.isRequired,
  showRemoveItemModal: PropTypes.bool,
  showHeaders: PropTypes.bool,
  showSkipOnceLink: PropTypes.bool,
  isOneTimeAddList: PropTypes.bool,
  showSubstitutionButton: PropTypes.bool,
  showDeleteIcon: PropTypes.bool,
  cancellationReasons: PropTypes.array,
  displayItemFrequency: PropTypes.bool,
  editableQuantity: PropTypes.bool,
  displaySubstitutionCheckbox: PropTypes.bool,
  displayCancelButton: PropTypes.bool,
  loading: PropTypes.bool,
  refreshHandler: PropTypes.func,
  apiError: PropTypes.bool,
  showEditSubstitution: PropTypes.oneOf(['NONE', 'UPCOMING_ORDER', 'ALL_ITEMS']),
  substitutionValue: PropTypes.oneOf(['Best Available', 'Substitute With', 'Do Not Substitute']),
  quantityResetOnError: PropTypes.bool,
  paymentFailure: PropTypes.bool,
  upcomingOrders: PropTypes.object,
  orderType: PropTypes.oneOf(['upcoming', 'future', 'all']),
};

ScheduledItemsList.defaultProps = {
  editableQuantity: true,
  showDeleteIcon: false,
  orderType: 'upcoming',
};

export default ScheduledItemsList;
